import Component from "../../lib/component";
import Tracker from "../../lib/tracker";
import {getTrackerOptions} from "../../lib/tracker_helpers";
import {NativeAppSignalEmitter} from "../../lib/native_app_signal_emitter";
import {PaneSlidesConfig} from "../../components/pane_slides/pane_slides";
import {HOST_VIEW_CLOSE_EVENT, HOST_VIEW_CONTENT_READY_EVENT} from "../../constants/host_view_events";

export const OnboardingConfig = {
    componentTypeKey: 'Onboarding',
    baseHtmlClass: 'onboarding',
    selector: '.onboarding',

    datasetConfigKey: 'onboarding__config',

    closeSelector: '.onboarding__close',
    continueSelector: '.onboarding__continue',

    readyHtmlClassModifier: 'ready',
};

/**
 * The one-time welcome carousel.
 *
 * It is the same component wherever it is shown — a standalone webview page in the native apps, or
 * injected into the web app's onboarding overlay (see online_app_onboarding.js). It knows nothing
 * about who hosts it: closing simply emits the generic `HostView-close` signal, which reaches the
 * native shell over the `campingnz://` signal channel and the overlay host via a `document` event.
 * So the seen-flag is written by whoever owns the surface — never here.
 *
 * The slides themselves are `pane_slides`, which carries the generic slide instrumentation
 * (`SLIDE_VIEWED`, `SLIDES_COMPLETED`). This component adds only what is particular to a
 * presented-and-dismissable surface: `SLIDES_PRESENTED`, `SLIDES_DISMISSED` and the CTA's
 * `BUTTON_SELECTED`.
 */
export class Onboarding extends Component {

    static get config() {
        return OnboardingConfig;
    }

    constructor(id_or_element) {
        super({
            element: id_or_element,
            config: Onboarding.config
        });

        this.setDatasetConfig(OnboardingConfig.datasetConfigKey);

        this.slidesId = this.getDatasetConfigValue('slides_id');
        this.contentVersion = this.getDatasetConfigValue('content_version');
        this.continueButtonId = this.getDatasetConfigValue('continue_button_id');

        this.tracker = new Tracker(window, document, getTrackerOptions(this.getDatasetConfigValue('tracker_options')) || {});

        const signalEmitterOptions = this.getDatasetConfigValue('native_app_signal_emitter_options');
        if (signalEmitterOptions) this.nativeAppSignalEmitter = new NativeAppSignalEmitter(signalEmitterOptions);

        this.isReady = false;
        this.hasCompleted = false;
        this.hasClosed = false;
        this.focusedSlide = {slideId: null, slideIndex: 0};

        this.listenToSlides();
        this.listenToControls();

        this.trackPresented();
        this.signalWhenContentReady();

        this.render();
    }

    render() {
        this.conditionallySetHtmlClassState(this.isReady, OnboardingConfig.readyHtmlClassModifier);
    }

    listenToSlides() {
        this.element.addEventListener(PaneSlidesConfig.slideFocusedEventName, (event) => {
            this.focusedSlide = event.detail;
        });
        this.element.addEventListener(PaneSlidesConfig.completedEventName, () => {
            this.hasCompleted = true; // `SLIDES_COMPLETED` is emitted by the carousel itself.
        });
    }

    listenToControls() {
        const closeElement = this.element.querySelector(OnboardingConfig.closeSelector);
        if (closeElement) closeElement.addEventListener('click', () => this.handleDismiss());

        const continueElement = this.element.querySelector(OnboardingConfig.continueSelector);
        if (continueElement) continueElement.addEventListener('click', () => this.handleContinue());
    }

    // The close cross. Reaching the last slide and then closing is a completion, not a bail — so
    // only report a dismissal when the traveller left before the end.
    handleDismiss() {
        if (!this.hasCompleted) {
            this.trackEvent('SLIDES_DISMISSED', this.focusedSlide.slideId, {
                slides_id: this.slidesId,
                slide_id: this.focusedSlide.slideId,
                slide_index: this.focusedSlide.slideIndex,
            });
        }
        this.close();
    }

    handleContinue() {
        this.trackEvent('BUTTON_SELECTED', this.continueButtonId, {button_id: this.continueButtonId});
        this.close();
    }

    close() {
        if (this.hasClosed) return;
        this.hasClosed = true;
        this.emitSignal(HOST_VIEW_CLOSE_EVENT);
    }

    // The native shells hold their splash cover until the content is ready, so the reveal never
    // shows a loading webview. Images only: the artwork is inlined in the native apps (nothing to
    // wait for) and fetched as `<img>` on the web.
    signalWhenContentReady() {
        const images = Array.from(this.element.querySelectorAll('img'));
        const pending = images.filter(image => !image.complete).map(image => new Promise((resolve) => {
            image.addEventListener('load', resolve, {once: true});
            image.addEventListener('error', resolve, {once: true}); // A missing image must not hold the splash forever.
        }));

        Promise.all(pending).then(() => {
            this.isReady = true;
            this.render();
            this.emitSignal(HOST_VIEW_CONTENT_READY_EVENT);
        });
    }

    // A signal reaches the native host over the `campingnz://` channel, and the page over a
    // `document` event. Only one of the two hosts exists at a time; neither has to know about the
    // other, so both are always told.
    emitSignal(signalName) {
        if (this.nativeAppSignalEmitter) this.nativeAppSignalEmitter.emit(signalName);
        document.dispatchEvent(new CustomEvent(signalName, {detail: this}));
    }

    trackPresented() {
        this.trackEvent('SLIDES_PRESENTED', this.slidesId, {
            slides_id: this.slidesId,
            content_version: this.contentVersion,
        });
    }

    // `eventLabel` is included for backwards compatibility with older native apps — see tracker.js
    // `getBackwardsCompatibilityParams()`.
    trackEvent(eventNameKey, eventLabel, parameters) {
        if (!window.RankersAnalytics || !window.RankersAnalytics.EventName) return;
        const eventName = window.RankersAnalytics.EventName[eventNameKey];
        if (!eventName || !this.tracker) return;
        this.tracker.eventTracker(eventName, {eventLabel: eventLabel, ...parameters});
    }

}
